
import * as React from 'react';
import { openItem } from '../../../operations/openItem';
import { currentItemFacet } from '../../../facets/ScopeFacets';
import { openActionControl } from '../../../controls/openActionControl';
import { connectToItemCache } from '../../decorators/connectToItemCache';
import { IItemCache } from '../../../models/IItemCacheObserver';
import { IActionProps, Action } from '../../action/Action';
import { withSelectedItems, withExactlyOneItem } from '../../decorators/selectors';
import { getItemsScope } from '../../decorators/connectToItemsScope';
import { getItemActionEngagement } from '../../../models/telemetry/getItemEngagement';
import { IItemCacheThunkAction } from '../../../store/actions/ItemCacheActions';

/**
 * @public
 */
export interface IItemOpenActionProps {
  itemKey: string | undefined;
}

/**
 * Action which sets the given item as the current item in the current items scope context.
 * @public
 */
export const ItemOpenAction: React.ComponentType<IItemOpenActionProps> = connectToItemCache((
  itemCache: IItemCache,
  props: IItemOpenActionProps
): IActionProps => {
  const {
    itemKey
  } = props;

  const { dispatch } = itemCache;

  const {
    itemsScopeItemKey
  } = getItemsScope(itemCache);

  const currentItem = itemCache.demandItemFacet(currentItemFacet, itemsScopeItemKey);
  const currentItemKey = currentItem && currentItem.itemKey;
  const controlHandler = itemCache.demandItemFacet(openActionControl, itemKey);

  if (!itemsScopeItemKey) {
    return {};
  }

  const {
    itemKey: finalItemKey,
    openItem: finalOpenItem,
    stageItem,
    isAvailable
  } = openActionControl.evaluate(controlHandler)(itemCache, {
    itemKey: itemKey,
    itemsScopeItemKey: itemsScopeItemKey,
    openItem: openItem,
    stageItem: defaultStageItem,
    isAvailable: true
  });

  const onExecute = finalItemKey ? async () => {
    const payload: typeof openItem.payloadType = {
      currentItemKey: finalItemKey,
      itemsScopeItemKey: itemsScopeItemKey
    };

    if (stageItem) {
      // Give addons a chance to supply the target item with data
      // to aid the final open process.
      await dispatch(stageItem(payload));
    }

    await dispatch(finalOpenItem(payload));
  } : undefined;

  return {
    isAvailable: !!finalItemKey && finalItemKey !== currentItemKey && isAvailable,
    isToggled: finalItemKey === currentItemKey,
    onExecute: onExecute,
    ...getItemActionEngagement(itemCache, {
      itemKey: finalItemKey,
      engagement: {
        name: 'Open',
        isIntentional: true
      }
    })
  };
})(Action);

/**
 * @public
 */
export const OpenAction: React.ComponentType<{}> = withSelectedItems()(withExactlyOneItem()(ItemOpenAction));

function defaultStageItem(payload: typeof openItem.payloadType): IItemCacheThunkAction<Promise<void>> {
  return async () => {
    // Do nothing.
  };
}
